import React, { useState } from "react";
import { motion } from "framer-motion";

const AGES = [
  {
    label: "0–6 meses",
    focus: "Lactancia y primeras texturas",
    desc: "Guía suave para acompañar la transición desde la leche materna o fórmula.",
    example: "Puré sedoso de zanahoria y manzana.",
  },
  {
    label: "6–12 meses",
    focus: "Inicio de la alimentación complementaria",
    desc: "Texturas, alergenos y combinaciones seguras para los primeros alimentos sólidos.",
    example: "Papilla de avena con plátano y aguacate.",
  },
  {
    label: "1–2 años",
    focus: "Construcción del paladar",
    desc: "Recetas variadas para que descubran sabores sin presión y con amor.",
    example: "Mini tortilla de espinaca y queso.",
  },
  {
    label: "2–4 años",
    focus: "Energía para crecer",
    desc: "Platos coloridos pensados para niños activos y curiosos.",
    example: "Pasta integral con salsa de tomate y zanahoria.",
  },
  {
    label: "4–6 años",
    focus: "Hábitos saludables",
    desc: "Recetas que fomentan autonomía y la elección consciente de alimentos.",
    example: "Wrap de pollo, palta y vegetales crocantes.",
  },
  {
    label: "6–12 años",
    focus: "Concentración y desarrollo",
    desc: "Comidas equilibradas para apoyar el cole, deportes y rutinas largas.",
    example: "Bowl de quinoa con salmón y vegetales asados.",
  },
  {
    label: "12–15 años",
    focus: "Energía adolescente",
    desc: "Opciones nutritivas, abundantes y deliciosas para esta etapa de cambios.",
    example: "Tacos suaves de pavo y guacamole casero.",
  },
];

export default function AgeTimeline() {
  const [active, setActive] = useState(2);

  return (
    <section
      id="por-edades"
      data-testid="age-timeline-section"
      className="bg-[#FBF4E7] py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#849983]">
            Organizado por edad
          </p>
          <h2 className="mt-4 font-serif-display text-4xl md:text-5xl tracking-tight leading-tight text-[#2A302A]">
            Recetas que crecen <em className="italic">con tus hijos</em>.
          </h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
          {/* Timeline navigation */}
          <div className="lg:col-span-5 relative">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[#E6E2D6]" />
            <ul className="space-y-2">
              {AGES.map((age, i) => {
                const isActive = active === i;
                return (
                  <li key={age.label}>
                    <button
                      data-testid={`age-tab-${i}`}
                      onClick={() => setActive(i)}
                      className="group flex items-center gap-4 w-full text-left py-3 pl-0 pr-4"
                    >
                      <span
                        className={`relative z-10 w-[30px] h-[30px] rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                          isActive
                            ? "bg-[#849983] border-[#849983] shadow-[0_4px_16px_rgba(132,153,131,0.4)]"
                            : "bg-white border-[#E6E2D6] group-hover:border-[#849983]"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            isActive ? "bg-white" : "bg-[#C8A892]"
                          }`}
                        />
                      </span>
                      <span
                        className={`font-serif-display text-xl md:text-2xl transition-colors ${
                          isActive ? "text-[#2A302A]" : "text-[#8A948A] group-hover:text-[#2A302A]"
                        }`}
                      >
                        {age.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Detail card */}
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 bg-white rounded-[2rem] p-8 md:p-12 border border-[#E6E2D6] shadow-[0_10px_40px_rgba(42,48,42,0.06)]"
            data-testid="age-detail-card"
          >
            <span className="inline-block text-[11px] tracking-[0.22em] uppercase font-semibold text-[#849983]">
              {AGES[active].focus}
            </span>
            <h3 className="mt-3 font-serif-display text-3xl md:text-4xl text-[#2A302A] leading-tight">
              {AGES[active].label}
            </h3>
            <p className="mt-5 text-base leading-relaxed text-[#576057]">
              {AGES[active].desc}
            </p>
            <div className="mt-8 border-t border-[#E6E2D6] pt-6">
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A948A] font-semibold">
                Ejemplo de receta
              </p>
              <p className="font-serif-display text-xl md:text-2xl text-[#2A302A] mt-2">
                {AGES[active].example}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
